import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, MessageSquare, X, Send, Lock, User, Clock, AlertTriangle, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_CONFIG = {
  low: { color: '#718096', bg: '#71809620' },
  medium: { color: '#D69E2E', bg: '#D69E2E20' },
  high: { color: '#E53E3E', bg: '#E53E3E20' },
  urgent: { color: '#C53030', bg: '#C5303025' }
};

const STATUS_CONFIG = {
  open: { color: '#3182CE', bg: '#3182CE20' },
  in_progress: { color: '#D69E2E', bg: '#D69E2E20' },
  waiting_customer: { color: '#9F7AEA', bg: '#9F7AEA20' },
  resolved: { color: '#38A169', bg: '#38A16920' },
  closed: { color: '#718096', bg: '#71809620' }
};

export default function SaaSTickets() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchTickets(); }, [filters]);
  useEffect(() => { if (ticketId) fetchTicketDetail(ticketId); }, [ticketId]);

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      const res = await axios.get(`${API}/saas/tickets?${params.toString()}`);
      setTickets(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const fetchTicketDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/saas/tickets/${id}`);
      setSelectedTicket(res.data);
    } catch { toast.error('Failed to load ticket'); }
  };

  const updateTicket = async (id, data) => {
    try {
      await axios.put(`${API}/saas/tickets/${id}`, data);
      toast.success('Ticket updated');
      fetchTickets();
      if (selectedTicket?.id === id) fetchTicketDetail(id);
    } catch { toast.error('Failed to update ticket'); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/saas/tickets/${selectedTicket.id}/messages`, {
        message: newMessage,
        author_type: 'admin',
        author_name: 'Super Admin',
        is_internal: isInternal
      });
      setNewMessage('');
      fetchTicketDetail(selectedTicket.id);
      toast.success('Message sent');
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  return (
    <div className="saas-page" data-testid="saas-tickets">
      <div className="saas-page-header">
        <div>
          <h2>Support Tickets</h2>
          <p>{total} tickets</p>
        </div>
      </div>

      <div className="saas-filters">
        <select value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))} className="filter-select" data-testid="filter-ticket-status">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters(p => ({ ...p, priority: e.target.value }))} className="filter-select" data-testid="filter-ticket-priority">
          <option value="">All Priorities</option>
          {Object.keys(PRIORITY_CONFIG).map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      <div className="saas-table-container">
        <table className="saas-table" data-testid="tickets-table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Tenant</th>
              <th>Subject</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(ticket => {
              const pc = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
              const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              return (
                <tr key={ticket.id} className="clickable-row" onClick={() => fetchTicketDetail(ticket.id)}>
                  <td><span className="ticket-number">{ticket.ticket_number}</span></td>
                  <td>{ticket.tenant_name || 'N/A'}</td>
                  <td className="subject-cell"><strong>{ticket.subject}</strong></td>
                  <td><span className="priority-badge" style={{ background: pc.bg, color: pc.color }}>{ticket.priority}</span></td>
                  <td><span className="status-badge" style={{ background: sc.bg, color: sc.color }}>{ticket.status.replace('_', ' ')}</span></td>
                  <td>{ticket.assigned_to || 'Unassigned'}</td>
                  <td className="timestamp-cell">{new Date(ticket.created_at).toLocaleDateString()}</td>
                  <td className="timestamp-cell">{new Date(ticket.updated_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {tickets.length === 0 && !loading && <div className="empty-state"><p>No tickets found</p></div>}
      </div>

      {selectedTicket && (
        <div className="slide-over-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="slide-over wide" onClick={e => e.stopPropagation()} data-testid="ticket-detail">
            <div className="slide-over-header">
              <div>
                <h3>{selectedTicket.ticket_number}</h3>
                <span className="ticket-subject">{selectedTicket.subject}</span>
              </div>
              <button className="close-btn" onClick={() => setSelectedTicket(null)}><X size={20} /></button>
            </div>

            <div className="slide-over-content">
              <section className="detail-section">
                <div className="ticket-meta-row">
                  <span className="priority-badge" style={{ background: PRIORITY_CONFIG[selectedTicket.priority]?.bg, color: PRIORITY_CONFIG[selectedTicket.priority]?.color }}>{selectedTicket.priority}</span>
                  <span className="status-badge" style={{ background: STATUS_CONFIG[selectedTicket.status]?.bg, color: STATUS_CONFIG[selectedTicket.status]?.color }}>{selectedTicket.status.replace('_', ' ')}</span>
                  <span className="meta-text">Tenant: {selectedTicket.tenant_name || 'N/A'}</span>
                  <span className="meta-text">By: {selectedTicket.requester_name || selectedTicket.requester_email || 'Unknown'}</span>
                </div>
              </section>

              {selectedTicket.description && (
                <section className="detail-section">
                  <h4>Description</h4>
                  <div className="ticket-description">{selectedTicket.description}</div>
                </section>
              )}

              <section className="detail-section">
                <h4>Conversation</h4>
                <div className="ticket-messages">
                  {(selectedTicket.messages || []).map(msg => (
                    <div key={msg.id} className={`ticket-message ${msg.author_type} ${msg.is_internal ? 'internal' : ''}`}>
                      <div className="message-header">
                        <span className="message-author">
                          {msg.author_type === 'admin' ? <User size={14} /> : <MessageSquare size={14} />}
                          {msg.author_name || msg.author_type}
                        </span>
                        {msg.is_internal && <span className="internal-badge"><Lock size={12} /> Internal</span>}
                        <span className="message-time">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <div className="message-body">{msg.message}</div>
                    </div>
                  ))}
                  {(!selectedTicket.messages || selectedTicket.messages.length === 0) && (
                    <div className="empty-state-small">No messages yet</div>
                  )}
                </div>

                <div className="message-composer">
                  <div className="composer-options">
                    <label className="internal-toggle">
                      <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                      <Lock size={14} />
                      Internal Note
                    </label>
                  </div>
                  <div className="composer-input">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={isInternal ? "Add an internal note..." : "Type a reply..."}
                      rows={3}
                      data-testid="ticket-message-input"
                    />
                    <button className="send-btn" onClick={sendMessage} disabled={sending || !newMessage.trim()} data-testid="send-ticket-message">
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </section>

              <div className="slide-over-actions">
                <select value={selectedTicket.status} onChange={(e) => updateTicket(selectedTicket.id, { status: e.target.value })} className="filter-select" data-testid="ticket-status-select">
                  {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
                <select value={selectedTicket.priority} onChange={(e) => updateTicket(selectedTicket.id, { priority: e.target.value })} className="filter-select" data-testid="ticket-priority-select">
                  {Object.keys(PRIORITY_CONFIG).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
